import React, { useEffect, useState } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'react-quill/dist/quill.snow.css';
import './Invoice.css';
import {
  UserCircle,
  BadgePlus,
  XCircle,
  FlaskConical,
  SearchCheck,
  CalendarDays,
  CircleDot,
  UserCheck,
  UserCog
} from 'lucide-react';
import { QRCodeSVG } from "qrcode.react";
import { motion } from "framer-motion";
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const ImagingQRcodeResult = () => {
  const [patientDetails, setPatientDetails] = useState(null);
  const [invalidData, setInvalidData] = useState(false);
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get("patientId");
  const invoiceNumber = searchParams.get("invoice_number");
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const [scanData, setScanData] = useState({});
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [systemData, setSystemData] = useState({
    phoneNumber: '',
    tinNumber: '',
    address: '',
    email: '',
    titleAmharic: '',
    titleEnglish: '',
  });

  useEffect(() => {
    // Fetch the current settings from the backend
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/setting/get-settings`);
        setSystemData(response.data);
      } catch (error) {
        toast.error("There is no setting data.");
        console.error(error);
      }
    };

    fetchSettings();
  }, []);


  useEffect(() => {
    fetch(`${BASE_URL}/patient/view-all-patients-based-on-ward/${patientId}`)
      .then((res) => res.json())
      .then((data) => {
        setPatientDetails(data);
      })
      .catch((err) => console.error("Error fetching patient details:", err));
  }, [patientId]);

  useEffect(() => {
    if (!token || !invoiceNumber || !patientId) {
      setInvalidData(true);
      setLoading(false);
      return;
    }
    const fetchScanData = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/other/qrcode-ultrasound-result`, {
          params: {
            invoiceNumber,
            patientId,
            token
          }
        });
        if (!response.data || response.data.length === 0) {
          setInvalidData(true);
        } else {
          // Group results by ultrasound_type
          const groupedData = response.data.reduce((acc, scan) => {
            if (!acc[scan.ultrasound_type]) {
              acc[scan.ultrasound_type] = [];
            }
            acc[scan.ultrasound_type].push(scan.result);
            return acc;
          }, {});

          setScanData(groupedData);
        }
        if (response.data.length > 0) {
          setNote(response.data[0].note);
        }
      } catch (error) {
        console.error("Error fetching scan data:", error);
      }
    };

    fetchScanData();
  }, [invoiceNumber]);

  const highlightKeywords = (text) => {
    const keywords = {
      'normal': 'text-green-600 font-semibold',
      'enlarged': 'text-red-600 font-semibold',
      'mass': 'text-red-600 font-semibold',
      'cyst': 'text-orange-500 font-semibold',
      'no evidence': 'text-green-600 font-semibold',
      'well defined': 'text-blue-600 font-semibold',
      'calcification': 'text-yellow-600 font-semibold',
      'shadowing': 'text-purple-600 font-semibold',
      'hypoechoic': 'text-pink-600 font-semibold',
      'echogenic': 'text-teal-600 font-semibold',
    };

    let highlighted = text;
    Object.entries(keywords).forEach(([word, cls]) => {
      const regex = new RegExp(`\\b(${word})\\b`, 'gi');
      highlighted = highlighted.replace(regex, `<span class="${cls}">$1</span>`);
    });

    return highlighted;
  };

  // 🚨 Invalid Data UI
  if (invalidData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-green-50">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="text-center p-8 rounded-2xl shadow-lg bg-white border border-green-300"
        >
          <motion.div
            animate={{ rotate: [0, -10, 10, -10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 2 }}
            className="flex justify-center"
          >
            <XCircle className="w-20 h-20 text-green-600" />
          </motion.div>
          <h2 className="mt-4 text-2xl font-bold text-green-700">Invalid Data</h2>
          <p className="text-green-600 mt-2">The medication order for this invoice could not be found.</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen w-[180%]">
      <div className="flex-grow text-right">
        <button
          onClick={() => window.print()}
          className="px-4 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 hidden-print"
        >
          Print
        </button>
      </div>
      <div className="max-w-7xl mx-auto p-2 bg-white border border-black rounded-lg shadow-md print-invoice">
        {/* Logo and Header Section */}
        <div className="text-center mb-4">
          <img src="/assets/hiywot_logo.jpg" alt="hiywot_logo" className="mx-auto h-24" />
          <div className="text-lg">
            <p className="text-base font-bold text-gray-800">
              <span className="font-semibold text-blue-600">Email:</span> {systemData.email} |

              <span className="font-bold text-blue-600">
                TIN <span className="underline">No</span>:
              </span>  {systemData.tinNumber} |

              <span className="font-bold text-blue-600">
                Phone <span className="underline">No</span> :
              </span> {systemData.phoneNumber} |
              <span className="font-bold text-blue-600"> Address:</span> {systemData.address}
            </p>
            <h2 className="text-center text-2xl font-extrabold underline text-gray-900">
              Imaging Report
            </h2>
          </div>
        </div>
        {/* Patient Info Section */}
        {patientDetails && (
          <div className="flex flex-col gap-2 text-sm text-gray-800 mb-4">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-1">
                <UserCircle className="text-blue-600 w-5 h-5" />
                <span className="font-semibold">Client:</span>
                <span>{patientDetails.name} {patientDetails.fname} {patientDetails.gfname}</span>
              </div>
              <div className="flex items-center gap-1">
                <BadgePlus className="text-green-600 w-5 h-5" />
                <span className="font-semibold">MRN:</span>
                <span>{patientDetails.mrn}</span>
              </div>

              <div className="flex items-center gap-1">
                <CalendarDays className="text-purple-600 w-5 h-5" />
                <span className="font-semibold">Age:</span>
                <span>{patientDetails.age}</span>
              </div>

              <div className="flex items-center gap-1">
                <CircleDot className="text-pink-500 w-5 h-5" />
                <span className="font-semibold">Sex:</span>
                <span>{patientDetails.sex}</span>
              </div>
            </div>

            <div className="flex items-center justify-between mt-1">
              <div className="flex items-center gap-1">
                <UserCheck className="text-blue-700 w-5 h-5" />
                <span className="font-semibold">Processed By:</span>
                <span>{patientDetails.processed_by}</span>
              </div>

              <div className="flex items-center gap-1">
                <UserCog className="text-emerald-600 w-5 h-5" />
                <span className="font-semibold">Ordered By:</span>
                <span>{patientDetails.ordered_by}</span>
              </div>
            </div>
          </div>
        )}
        {Object.keys(scanData).length > 0 ? (
          <div className="text-gray-700">
            {Object.entries(scanData).map(([type, results], index) => (
              <div key={index} className="mb-6">
                <h2 className="text-lg font-bold flex items-center gap-2 text-blue-700">
                  <FlaskConical className="w-5 h-5 text-blue-500" />
                  Type of Examination: <span className="text-gray-900">{type}</span>
                </h2>
                <p className="mt-2 flex items-center gap-2 text-green-700 font-semibold">
                  <SearchCheck className="w-4 h-4 text-green-600" />
                  Findings:
                </p>
                {results.map((result, i) => (
                  <p key={i} className="mb-2 leading-relaxed" dangerouslySetInnerHTML={{ __html: highlightKeywords(result) }}></p>
                ))}
              </div>
            ))}
            {note && (
              <div className="mt-4">
                <h2 className="text-xl font-bold underline mb-2">Conclusion:</h2>
                <p className="text-gray-800">{note}</p>
              </div>
            )}
          </div>
        ) : (
          <p className="text-gray-500">No Imaging results found.</p>
        )}
        <div className="flex items-center justify-between py-2">
          <div className="h-[10%] w-[10%]">
            <QRCodeSVG
              value={`https://amenmediumclinic.com/imaging-results?invoice_number=${invoiceNumber}&patientId=${patientId}&token=${token}`}
              size={128}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImagingQRcodeResult;
